import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { UserStoreState } from 'graph/state/query-hooks/use-user';
import {
  ProductCurriculum,
  UnitAssessment,
  BenchmarkAssessment,
  AssessmentReport,
  StudentReportStats,
  Remediation,
} from 'types';
import { FormattedAssessmentOutcomes } from 'types/formatted-assessment-outcomes';
import { getCurriculumBenchmarkAssessment } from 'util/assessments-util';

const t = (key) => translate('ui', key);

export const findProductCurriculum = (
  user: UserStoreState,
  productCurriculumId: number
): ProductCurriculum | undefined => {
  const found = user.me?.licenseUsage?.find(
    (usage) => usage.productCurriculumId === productCurriculumId
  )?.productCurriculum;

  return found ? found : undefined;
};

export const formatReports = (
  productCurriculumId: number,
  allDomains: any,
  reports: AssessmentReport[],
  domain: string,
  unit?: string,
  benchmark?: string
): FormattedAssessmentOutcomes[] => {
  if ((benchmark === 'all' || unit === 'all') && domain !== 'total') {
    return reports.map((report) => {
      const domainStats = report.reportDomains.filter((v) => v.domainId === parseInt(domain));

      const title = benchmark
        ? t(
            `report.benchmarkPeriod-${
              getCurriculumBenchmarkAssessment(
                report.assessment as BenchmarkAssessment,
                productCurriculumId
              ).period
            }`
          )
        : (report.assessment as UnitAssessment).unit.title;

      const id = benchmark
        ? getCurriculumBenchmarkAssessment(
            report.assessment as BenchmarkAssessment,
            productCurriculumId
          ).period
        : (report.assessment as UnitAssessment).unitId;

      return domainStats?.length > 0 && domainStats[0].stats
        ? makeOutcome(title, domainStats[0].stats, id, undefined, domainStats[0].remediations)
        : makeOutcome(title, [], id, report.assessmentId);
    });
  }

  if ((unit && unit !== 'all') || (benchmark && benchmark !== 'all')) {
    const filteredReport = reports.find(
      unit
        ? (v) => (v.assessment as UnitAssessment).unitId === parseInt(unit)
        : (v) =>
            getCurriculumBenchmarkAssessment(
              v.assessment as BenchmarkAssessment,
              productCurriculumId
            ).period === benchmark
    );

    if (domain === 'total') {
      const totalOutcome = filteredReport
        ? makeOutcome('Total', filteredReport!.totalStats, 'total')
        : makeOutcome('Total', [], 'total');
      return filteredReport
        ? [
            totalOutcome,
            ...allDomains.map((domain) => {
              const domainStats = filteredReport?.reportDomains.filter(
                (v) => v.domainId === domain.id
              );
              return domainStats?.length > 0 && domainStats[0]?.stats
                ? makeOutcome(
                    domain.title,
                    domainStats[0].stats,
                    domain.id,
                    undefined,
                    domainStats[0].remediations
                  )
                : makeOutcome(domain.title, [], domain.id, filteredReport.assessmentId);
            }),
          ]
        : [totalOutcome];
    } else {
      const domainStats = filteredReport?.reportDomains.filter(
        (v) => v.domainId === parseInt(domain)
      );
      const totalOutcome =
        domainStats && domainStats?.length > 0 && domainStats[0].stats
          ? makeOutcome(
              'Total',
              domainStats[0].stats,
              'total',
              undefined,
              domainStats[0].remediations
            )
          : makeOutcome('Total', [], 'total', filteredReport?.assessmentId);
      const uniqueSections = filteredReport?.assessment.assessmentDomains.find(
        (v) => v.domain.id === parseInt(domain)
      )?.assessmentSections;
      const sectionStats = filteredReport?.reportSections.filter((v) =>
        uniqueSections
          ?.map((section) => {
            return section.id;
          })
          .includes(v.assessmentSectionId)
      );

      return uniqueSections
        ? [
            totalOutcome,
            ...uniqueSections.map((section) => {
              const uniqueSectionStats = sectionStats?.filter(
                (v) => v.assessmentSectionId === section.id
              );
              return uniqueSectionStats &&
                uniqueSectionStats.length > 0 &&
                uniqueSectionStats[0].stats
                ? makeOutcome(
                    section.title!,
                    uniqueSectionStats[0].stats,
                    undefined,
                    undefined,
                    undefined,
                    section.id
                  )
                : makeOutcome(section.title!, [], undefined, undefined, undefined, section.id);
            }),
          ]
        : [totalOutcome];
    }
  }

  return reports.map((report) => {
    const title = benchmark
      ? t(
          `report.benchmarkPeriod-${
            getCurriculumBenchmarkAssessment(
              report.assessment as BenchmarkAssessment,
              productCurriculumId
            ).period
          }`
        )
      : (report.assessment as UnitAssessment).unit.title;

    const id = benchmark
      ? getCurriculumBenchmarkAssessment(
          report.assessment as BenchmarkAssessment,
          productCurriculumId
        ).period
      : (report.assessment as UnitAssessment).unitId;

    return makeOutcome(title, report.totalStats, id);
  });
};

const makeOutcome = (
  label: string,
  stats: StudentReportStats[],
  id?: number | string,
  assessmentId?: number,
  remediations?: Remediation[],
  sectionId?: number
) => {
  return {
    label: label,
    sectionId: sectionId,
    id: id,
    assessmentId: assessmentId,
    meets: stats.find((v) => v.score === 3) ? stats.find((v) => v.score === 3)!.studentIds : [],
    growing: stats.find((v) => v.score === 2) ? stats.find((v) => v.score === 2)!.studentIds : [],
    early: stats.find((v) => v.score === 1) ? stats.find((v) => v.score === 1)!.studentIds : [],
    notTested: stats.find((v) => v.score === null)
      ? stats.find((v) => v.score === null)!.studentIds
      : [],
    remediations: remediations,
  } as FormattedAssessmentOutcomes;
};
